/* eslint-disable import/no-named-as-default */
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import Accordion from 'yoda-core-components/lib/components/Accordion/accordion';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { bool } from 'prop-types';
import {
    isDesktopWidth,
    isTabletWidth,
    isMobileWidth,
} from 'yoda-core-components/lib/helpers/Utils/mediaQuery';
import * as styles from './FilterPanel.css';
import FilterCategory from '../FilterCategory/FilterCategory';
import SelectedFacets from '../SelectedFacets/SelectedFacets';
import StoreFilterCategory from '../StoreFilterCategory/StoreFilterCategory';
import updateMultiSelectFacetInfo from '../../actions/MultiSelectFilterActions';
import { getPageType, getURLParameterBykey, updateUrl } from '../../utils';

const cx = classNames.bind(styles);

export const FilterPanel = ({ inSlider, isGalleryFilter }) => {
    const {
        context: {
            featureFlags: {
                enableMultiSelectFilters = false,
                enableStickyFacet = false,
                enableBrandNameCasing = false,
            } = {},
        } = {},
        countOfSearchResults = 0,
        filters: { filterList = [] } = {},
        multiSelectFilter: { multiFacetUrl = '', multiSelectedFacetsList = {} } = {},
        sliderStatus = false,
    } = useSelector((store) => store);

    const dispatch = useDispatch();

    const hasFilters = filterList && filterList.length > 0;
    const isTabletView = isTabletWidth() && !sliderStatus;
    const isDesktopView = isDesktopWidth();
    const [expandOnSearch, setExpandOnSearch] = useState(false);
    const isMobileView = isMobileWidth();

    useEffect(() => {
        getPageType(window.location.href) === 's' &&
            setExpandOnSearch(
                filterList
                    .map(({ facetValueList = [] }) => facetValueList)
                    .reduce((previous, current) => [...previous, ...current], [])
                    .filter(
                        ({ solrFacetName = '' }) =>
                            !!getURLParameterBykey(solrFacetName, window.location.href, false)
                                .length
                    ).length
            );
    }, [filterList]);

    const facets = useMemo(() => {
        return filterList.map((facet) => {
            const {
                activeFacet = false,
                facetName = '',
                facetValueList = [],
                noOfSelectedFacetValues = 0,
            } = facet;

            const hasSelected = facetValueList.filter(({ selected }) => selected).length > 0;
            const selectedCount = noOfSelectedFacetValues;
            let header = facetName?.toLowerCase();
            let expanded = false;
            /* istanbul ignore next */
            if (expandOnSearch && (hasSelected || activeFacet)) expanded = true;
            else if (enableMultiSelectFilters) expanded = activeFacet;
            else expanded = hasSelected || activeFacet;

            if (selectedCount > 0) header += ` (${selectedCount})`;

            return {
                expanded,
                header,
                panel: <FilterCategory dataSource={facet} />,
                key: facetName,
            };
        });
    }, [filterList]);

    /* istanbul ignore next */
    const accordionTitleClicked = (event) => {
        const isExpanded = event.target.closest('button[aria-expanded]').ariaExpanded;
        if (
            isExpanded === 'false' &&
            enableMultiSelectFilters &&
            multiSelectedFacetsList &&
            Object.keys(multiSelectedFacetsList).length > 0 &&
            multiFacetUrl
        ) {
            dispatch(
                updateMultiSelectFacetInfo({
                    enableProductsLoader: true,
                    enableFacetLoader: true,
                    scrollToTitle: false,
                })
            );
            multiFacetUrl && updateUrl(multiFacetUrl, null, null, null);
        }
    };
    const filterPanelStyles = dt([
        'bg-white',
        'font-open-sans-regular',
        'h-full',
        'text-black',
        'text-small',
        'scrolling-touch',
    ]);
    const filterPanelDesktopNotTablet = isDesktopView && 'desktop';
    const filterPanelTablet = isTabletView && 'tablet';
    const filterPanelDesktop = dt(['lg:mr-4']);
    const filterPanelSliderStaus = sliderStatus && dt(['overflow-y-auto']);
    const filterPanelNotCase = !enableBrandNameCasing && dt(['lowercase']);
    const filterPanelAccordContainer =
        enableStickyFacet && isDesktopView
            ? `${dt(['overflow-y-auto'])} ${cx('filter-panel-container')}`
            : '';
    if (hasFilters) {
        return (
            <div
                className={cx(
                    'wrapper',
                    filterPanelStyles,
                    filterPanelDesktopNotTablet,
                    filterPanelTablet,
                    filterPanelDesktop,
                    filterPanelSliderStaus,
                    filterPanelNotCase
                )}
                data-automation-id="filter-panel"
            >
                <SelectedFacets
                    {...{
                        sliderStatus,
                        wrapperClass: cx('selectedFacets'),
                        inSlider,
                    }}
                />
                <StoreFilterCategory
                    {...{
                        customWrapperClasses: `${dt([
                            'border-gray-15',
                            'border-solid',
                            'border-t',
                            'py-4',
                            'px-3',
                            'smOnly:px-0',
                        ])} ${cx({ storeCustomClassTablet: isTabletView })}`,
                        inSlider,
                        sliderStatus,
                        parentSection: `${inSlider ? 'filterslider' : 'filterpanel'}`,
                    }}
                />
                <Accordion
                    automationId={`filterAccordian-${inSlider ? 'filterslider' : 'filterpanel'}`}
                    className={`${cx('filter-panel-accordion')} ${filterPanelAccordContainer}`}
                    onClick={accordionTitleClicked}
                    panelLeftIndent={false}
                    sectionUnderline
                    toggleControlLeft
                    toggleOnlyOne={enableMultiSelectFilters}
                    userToggle
                    isGalleryFilter={!!(hasFilters && isMobileView)}
                    parentSection={inSlider ? 'filterslider' : 'filterpanel'}
                    dynamicAccordionSections
                >
                    {facets}
                </Accordion>
            </div>
        );
    }
    if (!hasFilters && countOfSearchResults === 1) {
        return <>1 Result</>;
    }
    return null;
};

FilterPanel.propTypes = {
    inSlider: bool,
};

FilterPanel.defaultProps = {
    inSlider: false,
};

export default FilterPanel;
